import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { Navigator } from '@/presentation/routing/Navigator';
import {
  ExpauseButton,
  ButtonVariant,
} from '@/presentation/components/common/controls/ExpauseButton';
import { qaAttr } from '@/presentation/utils/qaAttr';

/**
 * Static header above the roadmap list.
 *
 * Ports the `SliverToBoxAdapter` header from `roadmap_screen.dart:519-618`:
 * the centered "Have a great idea…" disclaimer paragraph with an inline,
 * tappable "Idea Rewards terms" link, followed by the "Add idea" button.
 *
 * Parity notes (per the story's deferred-actions rule):
 * - The "Idea Rewards terms" link routes to the Terms page
 *   (`NavigationService.navigateToTermsScreen()` → `Navigator.navigateToTermsOfUse()`).
 * - "Add idea" navigates to the Add Roadmap (Add idea) page
 *   (Flutter `_onAddRoadmapItemTapped` → `NavigationService.navigateToAddRoadmapItemScreen()`,
 *   roadmap_screen.dart:137-142 → web `Navigator.navigateToAddRoadmap()`). The
 *   post-add reload is wired on `RoadmapPage` via the `ROADMAP_ITEM_ADDED` event.
 */
export const RoadmapDisclaimerHeader: React.FC = () => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();

  const handleRewardsLinkClick = () => {
    // TODO @Claude: implement Ideas Policy legal document on Flutter and Web
    Navigator.navigateToTermsOfUse();
  };

  const handleAddIdeaClick = () => {
    Navigator.navigateToAddRoadmap();
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        padding: responsive.spacing.base,
        paddingBottom: responsive.spacing['2xl'],
      }}
    >
      <p
        style={{
          margin: 0,
          textAlign: 'center',
          whiteSpace: 'pre-line',
          color: theme.disabled,
          fontSize: responsive.font.base,
          lineHeight: 1.5,
        }}
      >
        {l10n.roadmap.disclaimerIntro}
        <ExpauseButton
          variant={ButtonVariant.text}
          textColor={theme.text.primary}
          onClick={handleRewardsLinkClick}
          style={{
            display: 'inline',
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
          }}
          {...qaAttr({ id: 'roadmap-rewards-terms' })}
        >
          {l10n.roadmap.disclaimerRewardsLink}
        </ExpauseButton>
        {l10n.roadmap.disclaimerOutro}
      </p>

      <ExpauseButton
        variant={ButtonVariant.ghost}
        icon="/assets/images/add.png"
        iconColor={theme.disabled}
        onClick={handleAddIdeaClick}
        textColor={theme.text.primary}
        style={{
          marginTop: responsive.spacing.base,
          width: '100%',
          justifyContent: 'center',
          backgroundColor: theme.surface,
        }}
        {...qaAttr({ id: 'roadmap-add-idea' })}
      >
        {l10n.roadmap.addIdea}
      </ExpauseButton>
    </div>
  );
};
